import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check, Search } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

// City Pricing — contracts, bundles, and fees for existing business, while
// wasteops is built out. One page for both finance (create/edit) and
// customer service (view-only); which controls render is driven entirely
// by canManage below. Business data lives in a separate price_sheets
// database (backend/src/db/priceSheetsPool.js) — see that file's comment.
export default function CityPricing() {
  const { user } = useAuth();
  const canManage = user?.role === 'super_admin' || !!user?.canManagePriceSheets;

  const [filters, setFilters] = useState({ q: '', city: '', line_of_business: '', status: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const { data: lookups } = useQuery({
    queryKey: ['price-sheets-lookups'],
    queryFn: () => api.get('/contracts/lookups'),
  });

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => api.get('/contracts', { params: cleanParams(filters) }),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="City Pricing"
        subtitle="Contracts, bundles, and fees for existing business."
        actions={canManage && (
          <button style={s.primaryBtn} onClick={() => { setCreating(true); setSelectedId(null); }}>
            <Plus size={14} /> New Contract
          </button>
        )}
      />

      <FilterBar filters={filters} setFilters={setFilters} lookups={lookups} />

      <div style={s.layout}>
        <div style={s.listPane}>
          {isLoading && <div style={s.muted}>Loading…</div>}
          {!isLoading && contracts.length === 0 && <div style={s.muted}>No contracts match these filters.</div>}
          {contracts.map((c) => (
            <button
              key={c.id}
              style={{ ...s.contractRow, ...(selectedId === c.id ? s.contractRowActive : {}) }}
              onClick={() => { setSelectedId(c.id); setCreating(false); }}
            >
              <div>
                <div style={s.contractName}>{c.contract_name}</div>
                <div style={s.contractMeta}>{c.contract_number} · {c.city} · {c.line_of_business}</div>
              </div>
              <div style={s.contractRight}>
                <StatusPill status={c.status} />
                <span style={s.countBadge}>{c.bundle_count} bundle{c.bundle_count === '1' ? '' : 's'}</span>
              </div>
            </button>
          ))}
        </div>

        <div style={s.detailPane}>
          {creating && (
            <ContractForm
              canManage={canManage}
              lookups={lookups}
              onDone={(id) => { setCreating(false); setSelectedId(id); }}
              onCancel={() => setCreating(false)}
            />
          )}
          {!creating && selectedId && (
            <ContractDetail contractId={selectedId} canManage={canManage} lookups={lookups} />
          )}
          {!creating && !selectedId && (
            <div style={s.emptyState}>Select a contract to view its pricing.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function cleanParams(filters) {
  const out = {};
  for (const [k, v] of Object.entries(filters)) if (v) out[k] = v;
  return out;
}

function FilterBar({ filters, setFilters, lookups }) {
  return (
    <div style={s.filterBar}>
      <div style={s.searchBox}>
        <Search size={14} style={{ opacity: 0.5 }} />
        <input
          style={s.searchInput}
          placeholder="Search contract number or name…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
        />
      </div>
      <select style={s.select} value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}>
        <option value="">All cities</option>
        {(lookups?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select style={s.select} value={filters.line_of_business} onChange={(e) => setFilters((f) => ({ ...f, line_of_business: e.target.value }))}>
        <option value="">All lines of business</option>
        {(lookups?.linesOfBusiness || []).map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
      <select style={s.select} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
        <option value="">Active + inactive</option>
        <option value="active">Active only</option>
        <option value="inactive">Inactive only</option>
      </select>
    </div>
  );
}

function StatusPill({ status }) {
  const active = status === 'active';
  return (
    <span style={{ ...s.pill, background: active ? '#DCFCE7' : '#F3F4F6', color: active ? '#166534' : '#6B7280' }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
//  Contract create / edit
// ────────────────────────────────────────────────────────────────────────────

function ContractForm({ canManage, lookups, initial, onDone, onCancel }) {
  const qc = useQueryClient();
  const isCreate = !initial;
  const [form, setForm] = useState(isCreate
    ? { city: '', line_of_business: '' }
    : { contract_number: initial.contract_number, contract_name: initial.contract_name, city: initial.city, line_of_business: initial.line_of_business }
  );

  const save = useMutation({
    mutationFn: () => isCreate
      ? api.post('/contracts', { city: form.city, line_of_business: form.line_of_business })
      : api.put(`/contracts/${initial.id}`, form),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      if (initial) qc.invalidateQueries({ queryKey: ['contract', initial.id] });
      toast.success(isCreate ? 'Contract created.' : 'Contract updated.');
      onDone(data.id);
    },
    onError: (err) => toast.error(err.message),
  });

  if (!canManage) return null;

  const canSave = isCreate ? (form.city && form.line_of_business) : true;

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>{isCreate ? 'New Contract' : 'Edit Contract'}</div>
      <div style={s.formGrid}>
        {!isCreate && (
          <>
            <Field label="Contract Number">
              <input style={s.input} value={form.contract_number} onChange={(e) => setForm((f) => ({ ...f, contract_number: e.target.value }))} />
            </Field>
            <Field label="Contract Name">
              <input style={s.input} value={form.contract_name} onChange={(e) => setForm((f) => ({ ...f, contract_name: e.target.value }))} />
            </Field>
          </>
        )}
        <Field label="City">
          <select style={s.select} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}>
            <option value="">Select a city…</option>
            {(lookups?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Line of Business">
          <select style={s.select} value={form.line_of_business} onChange={(e) => setForm((f) => ({ ...f, line_of_business: e.target.value }))}>
            <option value="">Select…</option>
            {(lookups?.linesOfBusiness || []).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
      </div>
      {isCreate && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
          Contract number and name are generated automatically from the city and line of business.
        </div>
      )}
      <div style={s.formActions}>
        <button style={s.primaryBtn} disabled={save.isPending || !canSave} onClick={() => save.mutate()}>Save</button>
        <button style={s.secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
//  Contract detail — bundles/lines + fees
// ────────────────────────────────────────────────────────────────────────────

function ContractDetail({ contractId, canManage, lookups }) {
  const qc = useQueryClient();
  const [editingContract, setEditingContract] = useState(false);
  const [addingBundle, setAddingBundle] = useState(false);
  const [addingFee, setAddingFee] = useState(false);

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => api.get(`/contracts/${contractId}`),
  });

  const toggleStatus = useMutation({
    mutationFn: (status) => api.patch(`/contracts/${contractId}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Status updated.');
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading || !contract) return <div style={s.muted}>Loading…</div>;

  if (editingContract) {
    return (
      <ContractForm
        canManage={canManage}
        lookups={lookups}
        initial={contract}
        onDone={() => setEditingContract(false)}
        onCancel={() => setEditingContract(false)}
      />
    );
  }

  return (
    <div>
      <div style={s.card}>
        <div style={s.detailHeaderRow}>
          <div>
            <div style={s.cardTitle}>{contract.contract_name}</div>
            <div style={s.contractMeta}>{contract.contract_number} · {contract.city} · {contract.line_of_business}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusPill status={contract.status} />
            {canManage && (
              <>
                <button style={s.iconBtn} onClick={() => setEditingContract(true)} title="Edit contract"><Pencil size={14} /></button>
                <button
                  style={s.secondaryBtn}
                  onClick={() => toggleStatus.mutate(contract.status === 'active' ? 'inactive' : 'active')}
                >
                  Mark {contract.status === 'active' ? 'Inactive' : 'Active'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={s.sectionHeaderRow}>
        <div style={s.sectionTitle}>Bundles</div>
        {canManage && (
          <button style={s.secondaryBtn} onClick={() => setAddingBundle(true)}><Plus size={13} /> Add Bundle</button>
        )}
      </div>

      {addingBundle && (
        <BundleForm contractId={contractId} canManage={canManage} onDone={() => setAddingBundle(false)} onCancel={() => setAddingBundle(false)} />
      )}

      {contract.bundles.length === 0 && !addingBundle && <div style={s.muted}>No bundles yet.</div>}
      {contract.bundles.map((bundle) => (
        <BundleCard key={bundle.id} bundle={bundle} contractId={contractId} canManage={canManage} lookups={lookups} />
      ))}

      <OtherServicesSection contract={contract} contractId={contractId} canManage={canManage} lookups={lookups} />

      <div style={s.sectionHeaderRow}>
        <div style={s.sectionTitle}>Fees</div>
        {canManage && (
          <button style={s.secondaryBtn} onClick={() => setAddingFee(true)}><Plus size={13} /> Add Fee</button>
        )}
      </div>

      {addingFee && (
        <FeeForm contractId={contractId} onDone={() => setAddingFee(false)} onCancel={() => setAddingFee(false)} />
      )}

      {contract.fees.length === 0 && !addingFee && <div style={s.muted}>No fees.</div>}
      {contract.fees.length > 0 && (
        <div style={s.card}>
          {contract.fees.map((fee) => <FeeRow key={fee.id} fee={fee} canManage={canManage} />)}
        </div>
      )}
    </div>
  );
}

function BundleForm({ contractId, initial, onDone, onCancel }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial
    ? { name: initial.name, description: initial.description || '', unit_rate: initial.unit_rate ?? '', rate_unit: initial.rate_unit || 'per month' }
    : { name: '', description: '', unit_rate: '', rate_unit: 'per month' }
  );

  const save = useMutation({
    mutationFn: () => initial
      ? api.put(`/contracts/bundles/${initial.id}`, form)
      : api.post(`/contracts/${contractId}/bundles`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      toast.success(initial ? 'Bundle updated.' : 'Bundle added.');
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div style={s.card}>
      <div style={s.formGrid}>
        <Field label="Bundle Name">
          <input style={s.input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Residential 96gal Weekly" />
        </Field>
        <Field label="Description (optional)">
          <input style={s.input} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </Field>
        <Field label="Bundle Price ($)">
          <input style={s.input} type="number" step="0.01" min="0" value={form.unit_rate} onChange={(e) => setForm((f) => ({ ...f, unit_rate: e.target.value }))} placeholder="e.g. 45.00" />
        </Field>
        <Field label="Rate Unit">
          <input style={s.input} value={form.rate_unit} onChange={(e) => setForm((f) => ({ ...f, rate_unit: e.target.value }))} placeholder="per month" />
        </Field>
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
        This price covers everything in the bundle — its service lines describe what's included, not their own price.
      </div>
      <div style={s.formActions}>
        <button style={s.primaryBtn} disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
        <button style={s.secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function BundleCard({ bundle, contractId, canManage, lookups }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [addingLine, setAddingLine] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/contracts/bundles/${bundle.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      toast.success('Bundle removed.');
    },
    onError: (err) => toast.error(err.message),
  });

  if (editing) {
    return <BundleForm contractId={contractId} initial={bundle} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />;
  }

  return (
    <div style={s.card}>
      <div style={s.detailHeaderRow}>
        <div>
          <div style={s.bundleName}>{bundle.name}</div>
          {bundle.description && <div style={s.contractMeta}>{bundle.description}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={s.bundlePrice}>
            {bundle.unit_rate != null ? `$${Number(bundle.unit_rate).toFixed(2)} ${bundle.rate_unit}` : 'No price set'}
          </div>
          {canManage && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.iconBtn} onClick={() => setEditing(true)} title="Edit bundle"><Pencil size={14} /></button>
              <button style={s.iconBtnDanger} onClick={() => window.confirm(`Delete bundle "${bundle.name}"?`) && remove.mutate()} title="Delete bundle"><Trash2 size={14} /></button>
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
        The price above covers everything below — lines describe what's included, they aren't priced individually.
      </div>

      <div style={{ overflowX: 'auto' }}>
        {/* Wide table: it scrolls inside this, so the page does not. */}
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Service</th>
            <th style={s.th}>Container Type</th>
            <th style={s.th}>Container Size</th>
            <th style={s.th}>Waste Stream</th>
            <th style={s.th}>Frequency</th>
            {canManage && <th style={s.th}></th>}
          </tr>
        </thead>
        <tbody>
          {bundle.lines.map((line) => (
            <LineRow key={line.id} line={line} bundleId={bundle.id} contractId={contractId} canManage={canManage} lookups={lookups} showRate={false} />
          ))}
          {bundle.lines.length === 0 && (
            <tr><td colSpan={canManage ? 6 : 5} style={{ ...s.td, ...s.muted }}>No service lines yet.</td></tr>
          )}
        </tbody>
      </table>
      </div>

      {canManage && !addingLine && (
        <button style={s.secondaryBtn} onClick={() => setAddingLine(true)}><Plus size={13} /> Add Service Line</button>
      )}
      {addingLine && (
        <LineForm bundleId={bundle.id} contractId={contractId} lookups={lookups} showRate={false} onDone={() => setAddingLine(false)} onCancel={() => setAddingLine(false)} />
      )}
    </div>
  );
}

// Services priced directly on the contract, not part of any bundle.
function OtherServicesSection({ contract, contractId, canManage, lookups }) {
  const [addingLine, setAddingLine] = useState(false);
  const lines = contract.standaloneLines || [];

  return (
    <>
      <div style={s.sectionHeaderRow}>
        <div style={s.sectionTitle}>Other Services</div>
        {canManage && (
          <button style={s.secondaryBtn} onClick={() => setAddingLine(true)}><Plus size={13} /> Add Service Line</button>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: -4, marginBottom: 8 }}>
        For a single priced service that isn't part of a bundle.
      </div>

      {addingLine && (
        <LineForm contractId={contractId} lookups={lookups} showRate={true} onDone={() => setAddingLine(false)} onCancel={() => setAddingLine(false)} />
      )}

      {lines.length === 0 && !addingLine && <div style={s.muted}>No standalone services.</div>}
      {lines.length > 0 && (
        <div style={s.card}>
          <div style={{ overflowX: 'auto' }}>
            {/* Wide table: it scrolls inside this, so the page does not. */}
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Service</th>
                <th style={s.th}>Container Type</th>
                <th style={s.th}>Container Size</th>
                <th style={s.th}>Waste Stream</th>
                <th style={s.th}>Frequency</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Rate</th>
                {canManage && <th style={s.th}></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <LineRow key={line.id} line={line} contractId={contractId} canManage={canManage} lookups={lookups} showRate={true} />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </>
  );
}

// Falls back to the structured fields (e.g. "Cart · 96 Gallons · General
// Waste · 1x/week") when service_description is blank — optional on bundle
// lines, since those fields alone are usually enough of a label.
function lineDisplayLabel(line) {
  if (line.service_description) return line.service_description;
  const parts = [line.bin_type, line.bin_size, line.waste_stream, line.service_frequency].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function LineRow({ line, bundleId, contractId, canManage, lookups, showRate }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const colCount = (showRate ? 6 : 5) + (canManage ? 1 : 0);

  const remove = useMutation({
    mutationFn: () => api.delete(`/contracts/lines/${line.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      toast.success('Service line removed.');
    },
    onError: (err) => toast.error(err.message),
  });

  if (editing) {
    return (
      <tr>
        <td colSpan={colCount} style={s.td}>
          <LineForm bundleId={bundleId} contractId={contractId} lookups={lookups} showRate={showRate} initial={line} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} inline />
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={s.td}>{lineDisplayLabel(line)}</td>
      <td style={s.td}>{line.bin_type || '—'}</td>
      <td style={s.td}>{line.bin_size || '—'}</td>
      <td style={s.td}>{line.waste_stream || '—'}</td>
      <td style={s.td}>{line.service_frequency || '—'}</td>
      {showRate && <td style={{ ...s.td, textAlign: 'right' }}>${Number(line.unit_rate).toFixed(2)} {line.rate_unit}</td>}
      {canManage && (
        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
          <button style={s.iconBtn} onClick={() => setEditing(true)} title="Edit"><Pencil size={13} /></button>
          <button style={s.iconBtnDanger} onClick={() => window.confirm('Delete this service line?') && remove.mutate()} title="Delete"><Trash2 size={13} /></button>
        </td>
      )}
    </tr>
  );
}

function LineForm({ bundleId, contractId, initial, lookups, showRate, onDone, onCancel, inline }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial || {
    service_description: '', bin_type: '', bin_size: '', waste_stream: '', service_frequency: '', unit_rate: '', rate_unit: 'per month',
  });

  const save = useMutation({
    mutationFn: () => initial
      ? api.put(`/contracts/lines/${initial.id}`, form)
      : bundleId
        ? api.post(`/contracts/bundles/${bundleId}/lines`, form)
        : api.post(`/contracts/${contractId}/lines`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      toast.success(initial ? 'Service line updated.' : 'Service line added.');
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div style={inline ? {} : s.card}>
      <div style={s.formGridWide}>
        <Field label={showRate ? 'Service Description' : 'Service Description (optional)'}>
          <input style={s.input} value={form.service_description} onChange={(e) => setForm((f) => ({ ...f, service_description: e.target.value }))} placeholder={showRate ? 'e.g. Bulky Item Pickup' : 'Optional — container type/size/stream/frequency below usually suffice'} />
        </Field>
        <Field label="Container Type">
          <select style={s.select} value={form.bin_type || ''} onChange={(e) => setForm((f) => ({ ...f, bin_type: e.target.value }))}>
            <option value="">—</option>
            {(lookups?.binTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Container Size">
          <select style={s.select} value={form.bin_size || ''} onChange={(e) => setForm((f) => ({ ...f, bin_size: e.target.value }))}>
            <option value="">—</option>
            {(lookups?.binSizes || []).map((sz) => <option key={sz} value={sz}>{sz}</option>)}
          </select>
        </Field>
        <Field label="Waste Stream">
          <select style={s.select} value={form.waste_stream || ''} onChange={(e) => setForm((f) => ({ ...f, waste_stream: e.target.value }))}>
            <option value="">—</option>
            {(lookups?.wasteStreams || []).map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Service Frequency">
          <select style={s.select} value={form.service_frequency || ''} onChange={(e) => setForm((f) => ({ ...f, service_frequency: e.target.value }))}>
            <option value="">—</option>
            {(lookups?.serviceFrequencies || []).map((freq) => <option key={freq} value={freq}>{freq}</option>)}
          </select>
        </Field>
        {showRate && (
          <>
            <Field label="Unit Rate ($)">
              <input style={s.input} type="number" step="0.01" min="0" value={form.unit_rate} onChange={(e) => setForm((f) => ({ ...f, unit_rate: e.target.value }))} />
            </Field>
            <Field label="Rate Unit">
              <input style={s.input} value={form.rate_unit} onChange={(e) => setForm((f) => ({ ...f, rate_unit: e.target.value }))} placeholder="per month" />
            </Field>
          </>
        )}
      </div>
      {!showRate && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
          No price here — this line is covered by the bundle's price above.
        </div>
      )}
      <div style={s.formActions}>
        <button style={s.primaryBtn} disabled={save.isPending} onClick={() => save.mutate()}><Check size={13} /> Save</button>
        <button style={s.secondaryBtn} onClick={onCancel}><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
//  Fees
// ────────────────────────────────────────────────────────────────────────────

function FeeForm({ contractId, initial, onDone, onCancel }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial || { name: '', fee_type: 'flat', amount: '' });

  const save = useMutation({
    mutationFn: () => initial
      ? api.put(`/contracts/fees/${initial.id}`, form)
      : api.post(`/contracts/${contractId}/fees`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      toast.success(initial ? 'Fee updated.' : 'Fee added.');
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div style={s.card}>
      <div style={s.formGrid}>
        <Field label="Fee Name">
          <input style={s.input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Environmental Fee" />
        </Field>
        <Field label="Type">
          <select style={s.select} value={form.fee_type} onChange={(e) => setForm((f) => ({ ...f, fee_type: e.target.value }))}>
            <option value="flat">Flat $</option>
            <option value="percent">Percent %</option>
          </select>
        </Field>
        <Field label="Amount">
          <input style={s.input} type="number" step="0.0001" min="0" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </Field>
      </div>
      <div style={s.formActions}>
        <button style={s.primaryBtn} disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
        <button style={s.secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function FeeRow({ fee, canManage }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/contracts/fees/${fee.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract', fee.contract_id] });
      toast.success('Fee removed.');
    },
    onError: (err) => toast.error(err.message),
  });

  if (editing) {
    return <FeeForm contractId={fee.contract_id} initial={fee} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />;
  }

  return (
    <div style={s.feeRow}>
      <div>
        <div style={s.bundleName}>{fee.name}</div>
        <div style={s.contractMeta}>{fee.fee_type === 'flat' ? `$${Number(fee.amount).toFixed(2)} flat` : `${Number(fee.amount)}%`}</div>
      </div>
      {canManage && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.iconBtn} onClick={() => setEditing(true)}><Pencil size={13} /></button>
          <button style={s.iconBtnDanger} onClick={() => window.confirm(`Delete fee "${fee.name}"?`) && remove.mutate()}><Trash2 size={13} /></button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={s.fieldLabel}>
      {label}
      {children}
    </label>
  );
}

const s = {
  filterBar: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 12px', minWidth: 260 },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'inherit' },
  select: { border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: '#fff', fontFamily: 'inherit' },
  layout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' },
  listPane: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' },
  contractRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left',
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  contractRowActive: { borderColor: '#1B2A4A', boxShadow: '0 0 0 1px #1B2A4A' },
  contractName: { fontSize: 13, fontWeight: 600, color: '#111827' },
  contractMeta: { fontSize: 11, color: '#6B7280', marginTop: 2 },
  contractRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  countBadge: { fontSize: 10, color: '#9CA3AF' },
  pill: { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999 },
  detailPane: { minWidth: 0 },
  card: { ...card.sm, padding: 16, marginBottom: 12 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#1B2A4A' },
  detailHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  sectionHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0 8px' },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
  bundleName: { fontSize: 14, fontWeight: 600, color: '#111827' },
  bundlePrice: { fontSize: 14, fontWeight: 700, color: '#1B2A4A', whiteSpace: 'nowrap' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  formGridWide: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 },
  formActions: { display: 'flex', gap: 8 },
  fieldLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: '#6B7280' },
  input: { border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit' },
  primaryBtn: { ...btn.primary },
  secondaryBtn: { ...btn.secondarySm },
  iconBtn: { ...btn.iconBordered },
  iconBtnDanger: { ...btn.iconDanger, marginLeft: 6 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 8, marginBottom: 10 },
  th: { ...tbl.thSm, fontSize: 10, borderBottom: '1px solid #F3F4F6' },
  td: { ...tbl.td, fontSize: 13, color: '#111827', borderBottom: '1px solid #F9FAFB' },
  feeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' },
  muted: { fontSize: 13, color: '#9CA3AF', padding: '8px 0' },
  emptyState: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '60px 0' },
};
